import { Grid2, Box } from "@mui/material";
import moment from "moment";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { dailyLogApi, timesheetApi, workScheduleApi } from "@api";
import { MESSAGE_DATA_NOT_FOUND, OT_STATUS_PENDING, OT_STATUS_REJECT } from "@configs";
import { appRouteEnums, menuTitleEnums, WorkTypeNameEnums } from "@enums";
import { DailyViewTableComponent, StyledDailogView } from ".";
import { TitleMenuComponent, AppDateRangePicker, CircularLoader, AppInput, AppCustomeSelect, PassChangeButton } from "..";
import { Formik } from "formik";
import { dailyLogFilterSchema, useAppNavigation } from "@utils";
import { selectAuth, useAppSelector } from "@redux";
import useContractors from "src/Utils/Hooks/useContractors";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useConstructionSelectionHistories } from "src/Utils/Hooks/useConstructionSelectionHistories";


export const DailylogView = () => {
  // component state
  const auth = useAppSelector(selectAuth);
  const id = auth.userInfo?.id;
  const [dailylogData, setDailyLogData] = React.useState<any>();
  const [loading, setLoading] = React.useState(false);
  const [optionSiteConstructions, setOptionSiteConstructions] = React.useState<any>();

  // URLを真実の源泉にするため、初期値は当日範囲とする（localStorageは使用しない）
  const [dateRate, setDateRate] = React.useState<any>({ startDate: new Date(), endDate: new Date() });
  const [listWorkTypes, setListWorkTypes] = React.useState<any>();
  const [optionConstractors, setOptionConstractors] = React.useState<any>();
  const [optionEmployees, setOptionEmployees] = React.useState<any>();

  const { list, fetchList, storeSelection } = useUserSelectionHistories(id ?? 0);
  const { constructionList, fetchConstructionList, storeConstructionSelection } = useConstructionSelectionHistories(id ?? 0);

  // URLクエリ操作のための共通フック
  const { getQueryParam, currentPath, currentQueryString, navigateWithState, getAllQueryParams } = useAppNavigation();

  // デバウンス用（人数フィルタ）
  // 理由: 数字入力はタイプごとに値が変わるため、そのたびにURL更新・履歴pushを行うとUXが悪化する。
  //      ここでは「入力が止まってから一定時間後に一度だけ」URLを更新することで履歴のノイズを削減する。
  const minPeopleDebounceRef = React.useRef<any>(null);
  const maxPeopleDebounceRef = React.useRef<any>(null);
  const DEBOUNCE_MS = 500;

  const initialValuesPackage = {
    siteConstruction: "",
    constructors: "",
    minPeople: "",
    maxPeople: "",
    typeOfWork: "",
    workType1: "",
    workType2: "",
    workType3: "",
    userId: "",
  };

  // hooks
  const { updateContractorHistory } = useContractors();

  // 初回訪問時はデフォルト条件で実行（localStorageは使用しない）
  useEffect(() => {
    fetchLisDailyLog();
  }, []); // 依存配列を空にして初回のみ実行

  const history = useHistory();
  
  React.useEffect(() => {
    setOptionSiteConstructions(constructionList);
  },[constructionList]);

  React.useEffect(() => {
    setOptionEmployees(list);
  },[list]);

  React.useEffect(() => {
    fetchWorkTypeByType();
    return () => {
      // clean up
    };
  }, []);
  React.useEffect(() => {
    fetchListConstractors();
    return () => {
      // clean up
    };
  }, []);

  // fetch api daily log
  const fetchLisDailyLog = async (values?: any) => {
    setLoading(true);
    setDailyLogData("");
    try {
      const response = await dailyLogApi.filterDailyLog({
        from: moment(dateRate.startDate).format("YYYY-MM-DD"),
        to: moment(dateRate.endDate).format("YYYY-MM-DD"),
        constructionId: values?.siteConstruction?.value,
        contractorId: values?.constructors?.value,
        workType1: values?.workType1?.value,
        workType2: values?.workType2?.value,
        workType3: values?.workType3?.value,
        userId: values?.userId?.value,
        minEmployee: values?.minPeople,
        maxEmployee: values?.maxPeople,
      });
      setLoading(false);
      setDailyLogData(response.data.data);
    } catch (err: any) {
      setLoading(false);
      setDailyLogData("");
    }
  };
  const fetchWorkTypeByType = async () => {
    try {
      const result = await workScheduleApi.getWorkTypeByType();
      setListWorkTypes(result?.data?.data);
      // setOptionConstractors(result.data.data);
    } catch (err) { }
  };

  const fetchListConstractors = async () => {
    try {
      const result = await timesheetApi.getListContractors();
      setOptionConstractors(result.data.data.contractors);
    } catch (err) { }
  };

  // from=sidemenu を落としてからクエリを更新するヘルパー
  const navigateUpdatingQuery = React.useCallback((additionalParams: Record<string, any>) => {
    const currentParams = getAllQueryParams();
    if ('from' in currentParams) {
      delete currentParams.from;
    }
    const merged = { ...currentParams, ...additionalParams };
    // debug
    console.debug("[DailyLogDebug] navigateUpdatingQuery", { currentParams, additionalParams, merged, path: currentPath });
    navigateWithState(currentPath, merged, "push");
  }, [getAllQueryParams, navigateWithState, currentPath]);

  // events
  const handleChangeDateRange = (e) => {
    const newDateRange = { startDate: e.startDate, endDate: e.endDate };
    setDateRate(newDateRange);
    const start = moment(e.startDate).format("YYYY-MM-DD");
    const end = moment(e.endDate).format("YYYY-MM-DD");
    // 期間変更を履歴に積む（他条件は保持）
    navigateUpdatingQuery({ startDate: start, endDate: end });
  };

  const handleSearchParameterChange = (field: string, value, setFieldValue) => {
    setFieldValue(field, value);

    // フィールド -> クエリキーのマッピング
    const fieldToQueryKey: Record<string, string> = {
      siteConstruction: "constructionSiteId",
      constructors: "contractorId",
      userId: "userId",
      typeOfWork: "typeOfWork",
      workType1: "workType1",
      workType2: "workType2",
      workType3: "workType3",
      minPeople: "minPeople",
      maxPeople: "maxPeople",
    };
    const key = fieldToQueryKey[field] || field;

    // 選択肢はオブジェクト型のためvalue.valueを、数値/文字列はそのまま
    const normalized =
      value && typeof value === "object" && "value" in value ? value.value : (value ?? "");

    // クエリに反映（空文字はヘルパー側で除去）
    if (field === "minPeople" || field === "maxPeople") {
      // 最少/最大人数のみデバウンス対象
      // 理由: キー入力のたびに履歴pushを避ける（戻る操作の圧縮 & ネットワーク/レンダリング負荷の低減）
      const ref = field === "minPeople" ? minPeopleDebounceRef : maxPeopleDebounceRef;
      if (ref.current) {
        clearTimeout(ref.current);
      }
      ref.current = setTimeout(() => {
        // 入力が一定時間止まったと判断した時点でURLを更新（from=sidemenuはnavigateUpdatingQuery側で除去）
        navigateUpdatingQuery({ [key]: normalized });
      }, DEBOUNCE_MS);
    } else {
      navigateUpdatingQuery({ [key]: normalized });
    }
  };

  const handleViewDetail = (values: { otId; userId; status }) => {
    if (
      (id === values.userId && values.status === OT_STATUS_PENDING) ||
      (id === values.userId && values.status === OT_STATUS_REJECT)
    ) {
      history.push(`${appRouteEnums.PATH_OVERTIME}/edit/${values.otId}`);
    } else {
      history.push(`${appRouteEnums.PATH_OVERTIME}/${values.otId}`);
    }
  };
  return (
    <StyledDailogView>
      {/* <AppTopBar /> */}
      <CircularLoader loading={loading} />
      <div className="daily">
        <div className="daily__title">
          <TitleMenuComponent text={menuTitleEnums.TITLE_DAILY_LOG} />
        </div>
        <Formik
          initialValues={initialValuesPackage}
          onSubmit={(values) => {
            fetchLisDailyLog(values);
          }}
          validationSchema={dailyLogFilterSchema}
        >
          {({ handleSubmit, values, /*handleChange,*/ errors, touched, setFieldValue }) => {
            // URL -> 状態復元（戻る/進む対応）。各クエリをForm/Stateへ反映。
            React.useEffect(() => {
              // debug: 現在のクエリとフォーム値のスナップショット
              console.debug("[DailyLogDebug] currentQueryString", currentQueryString);
              // 日付
              const startDateParam = getQueryParam("startDate");
              const endDateParam = getQueryParam("endDate");
              // クエリが無い場合は「本日」を両方に入れる
              const today = new Date();
              const nextStart = startDateParam ? new Date(startDateParam) : today;
              const nextEnd = endDateParam ? new Date(endDateParam) : today;
              if (nextStart?.toString() !== dateRate.startDate?.toString() || nextEnd?.toString() !== dateRate.endDate?.toString()) {
                console.debug("[DailyLogDebug] setDateRate", { startDateParam, endDateParam, nextStart, nextEnd });
                setDateRate({ startDate: nextStart, endDate: nextEnd });
              }

              // 現場
              const constructionSiteId = getQueryParam("constructionSiteId");
              if (constructionSiteId && optionSiteConstructions?.length) {
                const target = optionSiteConstructions.find((i: any) => String(i.id) === String(constructionSiteId));
                const opt = target ? { label: target.name, value: String(target.id) } : null;
                if (opt && (values.siteConstruction as any)?.value !== opt.value) {
                  setFieldValue("siteConstruction", opt);
                }
                if (!constructionSiteId && values.siteConstruction) {
                  setFieldValue("siteConstruction", null);
                }
              } else if (!constructionSiteId && values.siteConstruction) {
                setFieldValue("siteConstruction", null);
              }

              // 請負業者
              const contractorId = getQueryParam("contractorId");
              if (contractorId && optionConstractors?.length) {
                const target = optionConstractors.find((i: any) => String(i.id) === String(contractorId));
                const opt = target ? { label: target.name, value: String(target.id) } : null;
                if (opt && (values.constructors as any)?.value !== opt.value) {
                  setFieldValue("constructors", opt);
                }
                if (!contractorId && values.constructors) {
                  setFieldValue("constructors", null);
                }
              } else if (!contractorId && values.constructors) {
                setFieldValue("constructors", null);
              }

              // 従業員
              const userIdParam = getQueryParam("userId");
              if (userIdParam && optionEmployees?.length) {
                const target = optionEmployees.find((i: any) => String(i.id) === String(userIdParam));
                const opt = target ? { label: `${target.last_name} ${target.first_name}`, value: String(target.id) } : null;
                if (opt && (values.userId as any)?.value !== opt.value) {
                  setFieldValue("userId", opt);
                }
                if (!userIdParam && values.userId) {
                  setFieldValue("userId", null);
                }
              } else if (!userIdParam && values.userId) {
                setFieldValue("userId", null);
              }

              // 作業種別（typeOfWorkは現状UI未使用のためスキップ）
              const wt1 = getQueryParam("workType1");
              if (wt1 && listWorkTypes?.type_1?.length) {
                const target = listWorkTypes.type_1.find((i: any) => String(i.id) === String(wt1));
                const opt = target ? { label: target.name, value: String(target.id), color: target.color } : null;
                if (opt && (values.workType1 as any)?.value !== opt.value) {
                  setFieldValue("workType1", opt);
                }
              } else if (!wt1 && values.workType1) {
                setFieldValue("workType1", null);
              }

              const wt2 = getQueryParam("workType2");
              if (wt2 && listWorkTypes?.type_2?.length) {
                const target = listWorkTypes.type_2.find((i: any) => String(i.id) === String(wt2));
                const opt = target ? { label: target.name, value: String(target.id), color: target.color } : null;
                if (opt && (values.workType2 as any)?.value !== opt.value) {
                  setFieldValue("workType2", opt);
                }
              } else if (!wt2 && values.workType2) {
                setFieldValue("workType2", null);
              }

              const wt3 = getQueryParam("workType3");
              if (wt3 && listWorkTypes?.type_3?.length) {
                const target = listWorkTypes.type_3.find((i: any) => String(i.id) === String(wt3));
                const opt = target ? { label: target.name, value: String(target.id), color: target.color } : null;
                if (opt && (values.workType3 as any)?.value !== opt.value) {
                  setFieldValue("workType3", opt);
                }
              } else if (!wt3 && values.workType3) {
                setFieldValue("workType3", null);
              }

              // 人数（数値に変換可能なら反映）
              const minPeopleParam = getQueryParam("minPeople");
              const maxPeopleParam = getQueryParam("maxPeople");
              const toNum = (v: string | null) => (v !== null && v !== "" && !Number.isNaN(Number(v)) ? Number(v) : "");
              const npMin = toNum(minPeopleParam);
              const npMax = toNum(maxPeopleParam);
              console.debug("[DailyLogDebug] numbers before set", { minPeopleParam, maxPeopleParam, npMin, npMax, formMin: values.minPeople, formMax: values.maxPeople });
              if (minPeopleParam !== null && values.minPeople !== npMin) setFieldValue("minPeople", npMin);
              if (maxPeopleParam !== null && values.maxPeople !== npMax) setFieldValue("maxPeople", npMax);
              if (minPeopleParam === null && values.minPeople !== "") setFieldValue("minPeople", "");
              if (maxPeopleParam === null && values.maxPeople !== "") setFieldValue("maxPeople", "");
              if (minPeopleParam === null || maxPeopleParam === null) {
                console.debug("[DailyLogDebug] numbers after clear check", { formMinAfter: values.minPeople, formMaxAfter: values.maxPeople });
              }
            }, [currentQueryString, optionSiteConstructions, optionConstractors, optionEmployees, listWorkTypes]);
            return (
              <div className="searchContainer">
                <Grid2 container spacing={1}
                  justifyContent={"space-between"}>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <div className="wrapperDatePicker">
                      <AppDateRangePicker
                        value={{
                          startDate: dateRate?.startDate,
                          endDate: dateRate?.endDate,
                        }}
                        onChange={(e) => handleChangeDateRange(e)}
                        startText="開始"
                        endText="終了"
                      />
                    </div>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <div className="wrapperInput">
                      <div className="inputContent">
                        <label className="label">最小人数</label>
                        <AppInput
                          name="minPeople"
                          handleChange={(inputValue) => {
                            handleSearchParameterChange("minPeople", inputValue.toString(), setFieldValue);
                          }}
                          isNumber
                          value={values.minPeople === "" ? "" : values.minPeople}
                          error={errors.minPeople}
                          touched={touched.minPeople}
                        />
                      </div>
                      <div className="inputContent">
                        <label className="label">最大人数</label>
                        <AppInput
                          name="maxPeople"
                          handleChange={(inputValue) => {
                            handleSearchParameterChange("maxPeople", inputValue.toString(), setFieldValue);
                          }}
                          isNumber
                          value={values.maxPeople === "" ? "" : values.maxPeople}
                          error={errors.maxPeople}
                          touched={touched.maxPeople}
                        />
                      </div>
                    </div>
                  </Grid2>
                </Grid2>

                <Grid2 container spacing={1} marginTop={1}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    {/* <label className="label">現場名</label> */}
                    <div className="input select">
                      <AppCustomeSelect
                        placeholder="現場名"
                        name="siteConstruction"
                        value={values.siteConstruction}
                        groupOptions={null}
                        options={
                          optionSiteConstructions?.length &&
                          optionSiteConstructions?.map((item) => {
                            return {
                              label: item.name,
                              value: item.id.toString(),
                            };
                          })
                        }
                        onChange={(value) => {
                          handleSearchParameterChange(
                            "siteConstruction",
                            value,
                            setFieldValue
                          );
                          storeConstructionSelection({user_id: id, selected_construction_id: value.value });
                        }}
                        isClearable
                      />
                    </div>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <div className="input select">
                      <AppCustomeSelect
                        options={
                          optionConstractors?.length &&
                          optionConstractors?.map((item) => {
                            return {
                              label: item.name,
                              value: item.id.toString(),
                            };
                          })
                        }
                        name="constructors"
                        value={values.constructors}
                        placeholder="請負業者"
                        groupOptions={null}
                        onChange={(value) => {
                          updateContractorHistory(value);
                          handleSearchParameterChange(
                            "constructors",
                            value,
                            setFieldValue
                          );
                        }}
                        isClearable
                      />
                    </div>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <AppCustomeSelect
                      groupOptions={null}
                      options={
                        !!listWorkTypes?.type_1?.length &&
                        listWorkTypes?.type_1?.map((item) => {
                          return {
                            label: item.name,
                            value: item.id.toString(),
                            color: item.color,
                          };
                        })
                      }
                      name="workType1"
                      value={values.workType1}
                      placeholder={WorkTypeNameEnums.TYPE_1}
                      onChange={(value) => {
                        handleSearchParameterChange(
                          "workType1",
                          value,
                          setFieldValue
                        );
                      }}
                      isClearable
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <AppCustomeSelect
                      name="workType2"
                      value={values.workType2}
                      groupOptions={null}
                      options={
                        !!listWorkTypes?.type_2?.length &&
                        listWorkTypes?.type_2?.map((item) => {
                          return {
                            label: item.name,
                            value: item.id.toString(),
                            color: item.color,
                          };
                        })
                      }
                      onChange={(value) => {
                        handleSearchParameterChange(
                          "workType2",
                          value,
                          setFieldValue
                        );
                      }}
                      placeholder={WorkTypeNameEnums.TYPE_2}
                      isClearable
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <AppCustomeSelect
                      name="workType3"
                      value={values.workType3}
                      groupOptions={null}
                      options={
                        !!listWorkTypes?.type_3?.length &&
                        listWorkTypes?.type_3?.map((item) => {
                          return {
                            label: item.name,
                            value: item.id.toString(),
                            color: item.color,
                          };
                        })
                      }
                      onChange={(value) => {
                        handleSearchParameterChange(
                          "workType3",
                          value,
                          setFieldValue
                        );
                      }}
                      placeholder={WorkTypeNameEnums.TYPE_3}
                      isClearable
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <AppCustomeSelect
                      name="userId"
                      value={values.userId}
                      groupOptions={null}
                      options={
                        !!optionEmployees?.length &&
                        optionEmployees?.map((item) => {
                          return {
                            label: `${item.last_name} ${item.first_name}`,
                            value: item.id.toString(),
                            // avatar: linkImage(item?.avatar?.path),
                          };
                        })
                      }
                      onChange={(value) => {
                        handleSearchParameterChange(
                          "userId",
                          value,
                          setFieldValue
                        );
                        if (value) {
                          // console.log("value", value);
                          storeSelection({ user_id: id, selected_user_id: value.value });
                        }
                      }}
                      placeholder={"従業員"}
                      isClearable
                    />
                  </Grid2>

                </Grid2>

                <Box sx={{ paddingTop: "2rem" }}></Box>
                <Grid2 container>
                  <Grid2 size={{ xs: 12, sm: 12, md: 2, lg: 2 }}>
                    <PassChangeButton onClick={() => handleSubmit()} >検索する</PassChangeButton>
                  </Grid2>
                </Grid2>
              </div>
            );
          }}
        </Formik>

        {dailylogData?.timesheets?.length ? (
          <WrapTotal className="total">
            <div className="total__title">合計人数: </div>
            <div className="total__number">
              {dailylogData?.total_number_of_people
                ? dailylogData?.total_number_of_people
                : 0}
            </div>
          </WrapTotal>
        ) : (
          <></>
        )}
        {dailylogData?.timesheets?.length ? (
          <div>
            <DailyViewTableComponent
              data={dailylogData?.timesheets}
              handleViewDetail={handleViewDetail}
            />
          </div>
        ) : (
          <div className="errorMessage">
            <span>{MESSAGE_DATA_NOT_FOUND}</span>
          </div>
        )}
      </div>
    </StyledDailogView>
  );
};

const WrapTotal = styled.div`
    // right: 0;
    display:flex;
    justify-content:flex-end;

    // position: absolute;
    // margin-right: 17%;
    @media (max-width: ${(p) => p.theme.breakPoints.breakMedium}) {
        /* margin-right: 25%; */
        // display: none;
    }
    @media (min-width: ${(p) => p.theme.breakPoints.breakIpadPro}) {
        // margin-right: 21%;
    }
    @media (min-width: ${(p) => p.theme.breakPoints.breakBig}) {
        // margin-right: 17%;
    }
    .total__title {
        font-weight: bold;
    }
`;
